import { useEffect, useRef, useState } from "react";

/* ─────────────────────────────────────────────────────────────
   CountUp — animates a number from 0 → `to` when it scrolls
   into view. Uses an IntersectionObserver with a safety fallback
   so the final value is never left stuck at zero.
───────────────────────────────────────────────────────────── */
export default function CountUp({
  to,
  decimals = 0,
  duration = 1300,
  prefix = "",
  suffix = "",
}) {
  const ref = useRef(null);
  const started = useRef(false);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;

    const run = () => {
      if (started.current) return;
      started.current = true;

      // Respect users who prefer reduced motion.
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) {
        setValue(to);
        return;
      }

      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setValue(to * eased);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!el || typeof IntersectionObserver === "undefined") {
      setValue(to);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          run();
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);

    const fallback = window.setTimeout(run, 1800);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [to, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}
